import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  ArrowLeft, Archive, Spinner, CheckCircle, Trash, Storefront,
  Image as ImageIcon, ArrowUUpLeft,
} from "@phosphor-icons/react";
import BottomNav from "@/components/app/BottomNav";
import { useConfirm } from "@/components/app/ConfirmDialog";

export default function BulkArchivePage() {
  const confirm = useConfirm();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // sessionId or null
  const [sessionDetail, setSessionDetail] = useState(null);

  useEffect(() => {
    api.get("/bulk/sessions?archived=true")
      .then(({ data }) => setSessions(data || []))
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  }, []);

  const openSession = async (sid) => {
    if (expanded === sid) {
      setExpanded(null);
      setSessionDetail(null);
      return;
    }
    setExpanded(sid);
    setSessionDetail(null);
    try {
      const { data } = await api.get(`/bulk/sessions/${sid}/queue`);
      setSessionDetail(data);
    } catch {
      setSessionDetail({ error: true });
    }
  };

  const handleDelete = async (sid, e) => {
    e.stopPropagation();
    const ok = await confirm({
      title: "Permanently delete this session?",
      message: "Verdicts already matched will stay in your history. This can't be undone.",
      confirmLabel: "Delete forever",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/bulk/sessions/${sid}`);
      setSessions((prev) => prev.filter((s) => s.id !== sid));
      if (expanded === sid) { setExpanded(null); setSessionDetail(null); }
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to delete");
    }
  };

  const handleUnarchive = async (sid, e) => {
    e.stopPropagation();
    try {
      await api.post(`/bulk/sessions/${sid}/unarchive`);
      // Remove from the archived list — it's now back in the active resume list
      setSessions((prev) => prev.filter((s) => s.id !== sid));
      if (expanded === sid) { setExpanded(null); setSessionDetail(null); }
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to unarchive");
    }
  };

  return (
    <div className="min-h-screen bg-white pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b-4 border-black py-4 px-6 flex items-center gap-3">
        <Link to="/history" className="border-2 border-black p-2 active:translate-y-[1px]" data-testid="archive-back-btn">
          <ArrowLeft size={20} weight="bold" />
        </Link>
        <div className="flex items-center gap-2">
          <Archive size={20} weight="bold" />
          <h1 className="font-heading text-2xl font-black uppercase tracking-tighter">Archived Bulk Sessions</h1>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center">
          <Spinner size={28} weight="bold" className="animate-spin mx-auto text-neutral-400" />
        </div>
      ) : sessions.length === 0 ? (
        <div className="p-8 text-center" data-testid="archive-empty">
          <Archive size={48} weight="bold" className="mx-auto text-neutral-300 mb-3" />
          <div className="font-heading text-lg font-bold uppercase tracking-tight">No Archived Sessions</div>
          <p className="font-body text-sm text-neutral-500 mt-2">
            Finished bulk sessions will appear here automatically after all items have been listed.
          </p>
        </div>
      ) : (
        <div className="divide-y-2 divide-black">
          {sessions.map((s) => {
            const isOpen = expanded === s.id;
            return (
              <div key={s.id} className="bg-white" data-testid={`archived-session-${s.id}`}>
                <div className="flex items-stretch">
                  <button
                    onClick={() => openSession(s.id)}
                    className="flex-1 text-left px-4 py-3 hover:bg-neutral-50 active:bg-neutral-100"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#00E676]/20 border-2 border-[#00E676] flex items-center justify-center shrink-0">
                        <CheckCircle size={18} weight="bold" className="text-green-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-heading text-sm font-bold uppercase truncate">
                          {new Date(s.archived_at || s.updated_at || s.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                        </div>
                        <div className="font-body text-xs text-neutral-500 truncate">
                          {s.items_count || 0} item{(s.items_count || 0) !== 1 ? "s" : ""} · {s.photos_total} photo{s.photos_total !== 1 ? "s" : ""} · {new Date(s.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                        </div>
                      </div>
                      <span className="font-body text-[10px] text-neutral-400">{isOpen ? "▲" : "▼"}</span>
                    </div>
                  </button>
                  <button
                    onClick={(e) => handleUnarchive(s.id, e)}
                    className="px-3 border-l-2 border-black hover:bg-blue-50 active:bg-blue-100"
                    title="Unarchive — move back to resume list"
                    data-testid={`unarchive-${s.id}`}
                  >
                    <ArrowUUpLeft size={16} weight="bold" className="text-blue-700" />
                  </button>
                  <button
                    onClick={(e) => handleDelete(s.id, e)}
                    className="px-3 border-l-2 border-black hover:bg-red-50 active:bg-red-100"
                    title="Permanently delete"
                    data-testid={`delete-archived-${s.id}`}
                  >
                    <Trash size={16} weight="bold" className="text-red-600" />
                  </button>
                </div>

                {isOpen && (
                  <div className="px-4 pb-4 bg-neutral-50 border-t-2 border-dashed border-neutral-300">
                    {!sessionDetail ? (
                      <div className="py-6 text-center">
                        <Spinner size={20} weight="bold" className="animate-spin mx-auto text-neutral-400" />
                      </div>
                    ) : sessionDetail.error ? (
                      <div className="py-4 font-body text-xs text-red-600">Failed to load items.</div>
                    ) : (
                      <div className="pt-3 space-y-2">
                        <div className="font-heading text-[10px] font-bold uppercase tracking-wide text-neutral-600">
                          {sessionDetail.items_listed}/{sessionDetail.items_total} listed
                        </div>
                        {sessionDetail.items.map((item) => {
                          const v = item.verdict;
                          const firstPhoto = item.photos?.[0];
                          return (
                            <div key={item.sku} className="flex items-center gap-2 bg-white border-2 border-black p-2">
                              <div className="w-10 h-10 bg-neutral-100 border border-neutral-300 overflow-hidden shrink-0">
                                {firstPhoto ? (
                                  <img src={`/api/images/file/${firstPhoto.storage_path}`} className="w-full h-full object-cover" alt="" />
                                ) : (
                                  <ImageIcon size={18} weight="bold" className="text-neutral-300 mx-auto mt-2.5" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-heading text-xs font-bold uppercase truncate">
                                  {v?.brand || item.sku}
                                </div>
                                <div className="font-body text-[11px] text-neutral-500 truncate">
                                  {v?.title_keywords || "No verdict"}
                                </div>
                              </div>
                              {item.status === "listed" && v?.ebay_url ? (
                                <a
                                  href={v.ebay_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="shrink-0 bg-[#002FA7] text-white px-2 py-1 font-heading text-[9px] font-bold uppercase flex items-center gap-1"
                                  data-testid={`view-listing-${item.sku}`}
                                >
                                  <Storefront size={10} weight="bold" /> View
                                </a>
                              ) : (
                                <span className="shrink-0 font-heading text-[9px] font-bold uppercase text-neutral-400">
                                  {item.status === "listed" ? "Listed" : item.status}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <BottomNav />
    </div>
  );
}
